import React from "react";
import i18n from "../../../i18n";

import {
  MdOutlineImage,
  MdOutlineInsertDriveFile,
  MdOpenInNew,
  MdDownload,
  MdClose,
} from "react-icons/md";
import { useTranslation } from "react-i18next";

/**
 * One attachment, as a compact inline chip.
 *
 * Shared between the Notes feed (files already on the invoice) and the
 * Add/Modify editor (files picked but not yet sent), so a pending attachment
 * looks the same as a saved one and an admin can check what they are about to
 * put on a customer-visible invoice before pressing save.
 *
 * An image renders its own thumbnail rather than a generic glyph — the point is
 * to recognise the file, and "an image" is not a recognisable thing.
 */
export const AttachmentChip = ({
  name,
  meta,
  isImage,
  thumbUrl,
  onPreview,
  downloadHref,
  onRemove,
}) => {
  const { t } = useTranslation("admin");
  return (
  <span className="inline-flex h-6 flex-none items-center gap-1.5 rounded-[5px] border border-ui-border bg-ui-surface pl-[3px] pr-0.5 hover:border-ui-border-strong">
    <button
      type="button"
      title={isImage ? t("invoice.openPreview") : t("invoice.openInANewTab")}
      aria-label={t("invoice.previewName", { name })}
      onClick={onPreview}
      className="flex h-[18px] w-[18px] flex-none items-center justify-center overflow-hidden rounded-[3px] bg-state-neutral-bg text-ui-text-muted"
    >
      {isImage && thumbUrl ? (
        <img
          src={thumbUrl}
          alt=""
          className="h-full w-full object-cover"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      ) : isImage ? (
        <MdOutlineImage className="h-3 w-3" />
      ) : (
        <MdOutlineInsertDriveFile className="h-3 w-3" />
      )}
    </button>

    <span className="max-w-[140px] truncate font-manrope text-[11px] font-semibold text-ui-text">
      {name}
    </span>

    {meta && (
      <span className="font-manrope text-[10px] font-medium text-ui-text-muted">
        {meta}
      </span>
    )}

    <button
      type="button"
      title={isImage ? t("invoice.openPreview") : t("invoice.openInANewTab")}
      aria-label={t("invoice.openName", { name })}
      onClick={onPreview}
      className="flex h-5 w-5 flex-none items-center justify-center rounded border border-transparent text-ui-text-muted hover:bg-state-neutral-bg hover:text-ui-text"
    >
      <MdOpenInNew className="h-3 w-3" />
    </button>

    {downloadHref && (
      <a
        href={downloadHref}
        target="_blank"
        rel="noreferrer"
        title={t("common:actions.download")}
        aria-label={t("invoice.downloadName", { name })}
        className="flex h-5 w-5 flex-none items-center justify-center rounded border border-transparent text-ui-text-muted hover:bg-state-neutral-bg hover:text-ui-text"
      >
        <MdDownload className="h-3 w-3" />
      </a>
    )}

    {onRemove && (
      <button
        type="button"
        title={t("invoice.remove")}
        aria-label={t("invoice.removeName", { name })}
        onClick={onRemove}
        className="flex h-5 w-5 flex-none items-center justify-center rounded border border-transparent text-ui-text-muted hover:bg-state-danger-bg hover:text-state-danger-text"
      >
        <MdClose className="h-3 w-3" />
      </button>
    )}
  </span>
);
};

/** Readable size for a picked file — the API returns none for saved ones. */
export const fileSize = (bytes) => {
  if (!bytes && bytes !== 0) return null;
  if (bytes < 1024) return i18n.t("admin:invoice.sizeBytes", { size: bytes });
  if (bytes < 1024 * 1024)
    return i18n.t("admin:invoice.sizeKb", { size: Math.round(bytes / 1024) });
  return i18n.t("admin:invoice.sizeMb", { size: (bytes / (1024 * 1024)).toFixed(1) });
};

export default AttachmentChip;
